/**
 * Planning Feasibility Dashboard — the C-Mode projection of the Planning
 * surface (Doc 26, SPEC-02 planning want cases).
 *
 * Answers one root want for any tier of the organization:
 *   "Can we deliver our priorities?"
 *
 * Leaves rendered (SPEC-02 §4): feasibility answer card, above/below-line
 * list with plain-language reasons, levers, scenario comparison, resource
 * utilization, and drift signals on the approved plan. Every card links out
 * to the one B-Mode tab that edits its inputs — escape hatches, never
 * replication. View only: same engine, same rows, no new tables (rule 7).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  GitCompareArrows,
  Loader2,
  RefreshCw,
  Search,
  Signal,
  Wrench,
  Zap,
} from 'lucide-react';
import {
  engineInputLabel,
  getScenarios,
  readPersistedEngineInput,
  resolveEngineScopeSet,
  runEngineWithInput,
  runFeasibilityCheck,
} from '../../lib/planningEngineService';
import {
  itemsToPlanningEngineInput,
  LIVE_ITEMS_SCENARIO,
} from '../../lib/itemToPlanningAdapter';
import {
  runScenarioComparison,
  type ScenarioComparison,
} from '../../lib/planningScenarioComparison';
import {
  buildFeasibilityLevers,
  buildPriorityOutcomes,
  FEASIBILITY_PLAIN_LANGUAGE,
  feasibilityTone,
  type FeasibilityTone,
  type PriorityOutcome,
} from '../../lib/planningFeasibilityLanguage';
import {
  getActivePlanOfRecord,
  listDivergencesForPlanVersion,
  listPlanCycles,
  listReprioritizationTriggersForPlanCycle,
  type ReprioritizationTriggerRow,
} from '../../lib/planningService';
import { getItemCountState } from '../../components/collection/types';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { getItemEffortBandHours } from '../../stores/prioritization/planningDefaultsStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import ParticipationSummaryCard from './ParticipationSummaryCard';
import { useTimePhasedPlanningEnabled } from '../../features/time-phased-planning/hooks/useTimePhasedPlanningEnabled';
import type {
  PlanningEngineOutput,
  Scenario,
} from '../../lib/planningEngineModule.types';
import { PLANNING_MODE_LABELS } from '../../lib/planningEngineModule.types';
import type { PlanCycle, RankSequenceDivergence } from '../../types/planning';

// ---------------------------------------------------------------------------
// Tone → classes
// ---------------------------------------------------------------------------

const TONE_TEXT: Record<FeasibilityTone, string> = {
  green: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  amber: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
  red: 'text-[var(--coral)] dark:text-[var(--coral)]',
  neutral: 'text-[var(--text-ter)] dark:text-[var(--text-sec)]',
};

const TONE_BADGE: Record<FeasibilityTone, string> = {
  green: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]',
  amber: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]',
  red: 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]',
  neutral: 'bg-[var(--surface)] text-[var(--text-ter)] dark:bg-[var(--bg)]/20',
};

const CARD_CLASS =
  'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-charcoal-200 dark:border-[var(--rule)] ';

// ---------------------------------------------------------------------------
// Drift signals (approved plan only — pruned otherwise, SPEC-02 §3.1)
// ---------------------------------------------------------------------------

interface PlanSignals {
  cycle: PlanCycle;
  authorizedAt: string | null;
  triggers: ReprioritizationTriggerRow[];
  divergences: RankSequenceDivergence[];
}

async function loadPlanSignals(workspaceId: string | null): Promise<PlanSignals | null> {
  const { data: cycles } = await listPlanCycles({ workspaceId, status: 'active' });
  const cycle = cycles[0];
  if (!cycle) return null;
  const { data: por } = await getActivePlanOfRecord(cycle.id);
  if (!por) return null;
  const [{ data: triggers }, { data: divergences }] = await Promise.all([
    listReprioritizationTriggersForPlanCycle(cycle.id),
    listDivergencesForPlanVersion(por.plan_version_id),
  ]);
  return { cycle, authorizedAt: por.authorized_at ?? null, triggers, divergences };
}

// ---------------------------------------------------------------------------
// Priority outcome row
// ---------------------------------------------------------------------------

function OutcomeRow({ outcome }: { outcome: PriorityOutcome }) {
  const [open, setOpen] = useState(false);
  const { summary, plainReason, engineReason } = outcome;
  const tone = summary.hasParticipationRisk ? 'amber' : feasibilityTone(summary.feasibility);
  const hasDetail = Boolean(engineReason) || summary.requiredWU > 0;

  return (
    <div className="border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
      <button
        type="button"
        onClick={() => hasDetail && setOpen((v) => !v)}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left ${hasDetail ? 'hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]' : 'cursor-default'}`}
      >
        <span
          className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${
            tone === 'green' ? 'bg-[var(--green)]' : tone === 'amber' ? 'bg-[var(--mustard)]' : tone === 'red' ? 'bg-[var(--coral)]' : 'bg-[var(--rule-bright)]'
          }`}
        />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
            {summary.priority.name}
          </span>
          <span className={`block text-xs mt-0.5 ${TONE_TEXT[tone]}`}>{plainReason}</span>
        </span>
        <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${TONE_BADGE[feasibilityTone(summary.feasibility)]}`}>
          {FEASIBILITY_PLAIN_LANGUAGE[summary.feasibility]}
        </span>
        {hasDetail && (
          open
            ? <ChevronUp className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
            : <ChevronDown className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-9 pb-3 space-y-1">
          {summary.requiredWU > 0 && (
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
              {summary.includedWU.toFixed(1)} of {summary.requiredWU.toFixed(1)} work units fit
              ({summary.coveragePercent}% coverage).
            </p>
          )}
          {engineReason && (
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">{engineReason}</p>
          )}
          {summary.hasParticipationRisk && (
            <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
              Participation status: {summary.decisionWithParticipation}.{' '}
              <Link to="/planning/engine/participation" className="underline">
                See who needs to confirm
              </Link>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------

export default function PlanningFeasibilityDashboard() {
  const projectId = useTenantStore((s) => s.currentWorkspaceId);
  const userName = useUserStore((s) => s.userName);
  const timePhasedEnabled = useTimePhasedPlanningEnabled();
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const fetchEdges = useItemRelationshipsStore((s) => s.fetchFromSupabase);

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [scenariosLoaded, setScenariosLoaded] = useState(false);
  const [output, setOutput] = useState<PlanningEngineOutput | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comparison, setComparison] = useState<ScenarioComparison | null>(null);
  const [comparing, setComparing] = useState(false);
  const [signals, setSignals] = useState<PlanSignals | null>(null);
  const [query, setQuery] = useState('');
  const [expandedReasons, setExpandedReasons] = useState<Set<string>>(new Set());
  const autoRan = useRef(false);

  const activeScenario = useMemo(
    () => scenarios.find((s) => s.active) ?? scenarios[0] ?? null,
    [scenarios],
  );

  const checkFeasibility = useCallback(async (scenario: Scenario | null, forceRerun = false) => {
    if (!projectId) return;
    setRunning(true);
    setError(null);
    try {
      const engineInput = readPersistedEngineInput();
      if (scenario) {
        await Promise.all([fetchItems(), fetchEdges()]);
        const items = useItemsStore.getState().items;
        const edges = useItemRelationshipsStore.getState().edges;
        setOutput(
          await runFeasibilityCheck({
            projectId,
            scenario,
            engineInput,
            items,
            edges,
            forceRerun,
          }),
        );
      } else {
        // Recommended default when nothing is configured (guide moment):
        // run in memory against live items. No capacity model yet — the
        // levers card will point at defining one.
        await Promise.all([fetchItems(), fetchEdges()]);
        const items = useItemsStore.getState().items;
        const edges = useItemRelationshipsStore.getState().edges;
        setOutput(
          runEngineWithInput(
            itemsToPlanningEngineInput(
              items,
              edges,
              { ...LIVE_ITEMS_SCENARIO, projectId },
              getItemEffortBandHours(),
            ),
          ),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feasibility check failed');
    } finally {
      setRunning(false);
    }
  }, [projectId, fetchItems, fetchEdges]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    void getScenarios(projectId)
      .then((list) => {
        if (cancelled) return;
        setScenarios(list);
        setScenariosLoaded(true);
      })
      .catch(() => { if (!cancelled) setScenariosLoaded(true); });
    void loadPlanSignals(projectId).then((s) => { if (!cancelled) setSignals(s); });
    return () => { cancelled = true; };
  }, [projectId]);

  // Auto-run once so the answer is simply there (C Mode: zero required config).
  useEffect(() => {
    if (!scenariosLoaded || autoRan.current || !projectId) return;
    autoRan.current = true;
    void checkFeasibility(activeScenario);
  }, [scenariosLoaded, activeScenario, projectId, checkFeasibility]);

  const compare = useCallback(async () => {
    if (!projectId || scenarios.length < 2) return;
    setComparing(true);
    try {
      const engineInput = readPersistedEngineInput();
      await Promise.all([fetchItems(), fetchEdges()]);
      const items = useItemsStore.getState().items;
      const edges = useItemRelationshipsStore.getState().edges;

      let scopeSet: Set<string> | null = null;
      if (engineInput.type !== 'live') {
        scopeSet = await resolveEngineScopeSet(engineInput);
        if (!scopeSet || scopeSet.size === 0) {
          setError(
            `Input scope "${engineInputLabel(engineInput)}" resolved to 0 items. ` +
              'Check that the queue/pool/collection has members, then try again.',
          );
          return;
        }
      }

      setComparison(
        await runScenarioComparison(projectId, scenarios, items, edges, scopeSet, engineInput.type),
      );
    } finally {
      setComparing(false);
    }
  }, [projectId, scenarios, fetchItems, fetchEdges]);

  const outcomes = useMemo(() => (output ? buildPriorityOutcomes(output) : []), [output]);
  const levers = useMemo(() => (output ? buildFeasibilityLevers(output) : []), [output]);

  const filteredOutcomes = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return outcomes;
    return outcomes.filter((o) => o.summary.priority.name.toLowerCase().includes(q));
  }, [outcomes, query]);

  const above = filteredOutcomes.filter((o) => o.aboveLine);
  const below = filteredOutcomes.filter((o) => !o.aboveLine);
  const exec = output?.executiveSummary ?? null;

  // A long below-the-line list would bury the levers and comparison cards, so
  // past the "few" threshold it collapses into one group per reason. An active
  // search always shows the matching rows directly.
  const groupBelow = !query.trim() && getItemCountState(below.length) === 'many';
  const belowGroups = useMemo(() => {
    if (!groupBelow) return [];
    const m = new Map<string, PriorityOutcome[]>();
    for (const o of below) m.set(o.plainReason, [...(m.get(o.plainReason) ?? []), o]);
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [groupBelow, below]);

  const toggleReason = (reason: string) => {
    setExpandedReasons((prev) => {
      const next = new Set(prev);
      if (next.has(reason)) next.delete(reason);
      else next.add(reason);
      return next;
    });
  };

  // "The ball is always named" (Doc 26 rule 4).
  const ballLine = useMemo(() => {
    if (!output) return 'The ball is with you — check feasibility to get an answer.';
    const confirmations = (exec?.participationAtRiskCount ?? 0) + (exec?.participationBlockedCount ?? 0);
    if (confirmations > 0) {
      return `The ball is with the supporting teams — ${confirmations} confirmation${confirmations === 1 ? '' : 's'} outstanding.`;
    }
    const decisions = (exec?.deferredCount ?? 0) + (exec?.blockedCount ?? 0) + (exec?.notReadyCount ?? 0) + (exec?.noCapacityModelCount ?? 0);
    if (decisions > 0) {
      return `The ball is with you — ${decisions} item${decisions === 1 ? '' : 's'} need a decision (reorder, add capacity, resolve, or defer).`;
    }
    return signals
      ? 'Resolved — everything fits, and the approved plan holds.'
      : 'Resolved — everything fits under the current assumptions.';
  }, [output, exec, signals]);

  const openDivergences = signals?.divergences.filter(
    (d) => d.resolution_status === 'detected' || d.resolution_status === 'disputed',
  ) ?? [];
  const openTriggers = signals?.triggers.filter((t) => t.status !== 'resolved' && t.status !== 'dismissed') ?? [];

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      {/* Header — the root want */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-charcoal-900 dark:text-[var(--text-pri)]">
            Can we deliver our priorities?
          </h1>
          <p className="mt-1 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">{ballLine}</p>
        </div>
        <button
          type="button"
          onClick={() => void checkFeasibility(activeScenario, !!output)}
          disabled={running || !projectId}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : output ? <RefreshCw className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
          {output ? 'Check again' : 'Check feasibility'}
        </button>
      </div>

      {!activeScenario && scenariosLoaded && (
        <div className="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)] px-4 py-3 text-sm text-[var(--text-sec)]">
          No planning scenario is set up yet, so this check uses recommended defaults: your live
          priorities and their size estimates, over a 12-week horizon. That's a fine starting
          point — refine it later in the{' '}
          <Link to="/planning/engine" className="underline font-medium">Planning Engine</Link>.
        </div>
      )}

      {error && (
        <div className="bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] px-4 py-3 text-sm text-[var(--coral)] dark:text-[var(--coral)]">
          {error}
        </div>
      )}

      {/* The answer */}
      {exec && output && (
        <div className={`${CARD_CLASS} p-6`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className={`text-2xl font-heading font-semibold ${TONE_TEXT[feasibilityTone(exec.overallFeasibility)]}`}>
                {FEASIBILITY_PLAIN_LANGUAGE[exec.overallFeasibility]}
              </div>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
                {activeScenario
                  ? `Scenario "${activeScenario.name}" · ${PLANNING_MODE_LABELS[activeScenario.planningMode]} · ${activeScenario.planningWeeks} weeks`
                  : 'Recommended defaults · live priorities'}
                {' · '}as of {new Date(output.generatedAt).toLocaleTimeString()}
              </p>
            </div>
            <div className="flex gap-6">
              {[
                { label: 'Deliverable', count: above.length, tone: 'green' as const },
                { label: 'At risk', count: outcomes.filter((o) => o.summary.hasParticipationRisk).length, tone: 'amber' as const },
                { label: "Doesn't fit", count: below.length, tone: 'red' as const },
              ].map(({ label, count, tone }) => (
                <div key={label} className="text-center">
                  <div className={`text-2xl font-heading font-semibold ${TONE_TEXT[tone]}`}>{count}</div>
                  <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Above / below the line */}
      {output && outcomes.length > 0 && (
        <div className={`${CARD_CLASS} overflow-hidden`}>
          <div className="px-4 pt-4 pb-2 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-charcoal-600 dark:text-[var(--text-sec)] uppercase tracking-wider">
              Where each priority lands
            </h2>
            {getItemCountState(outcomes.length) === 'many' && (
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search priorities…"
                  className="pl-8 pr-3 py-1.5 text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500 w-56"
                />
              </div>
            )}
          </div>
          {above.length > 0 && (
            <>
              <div className="px-4 py-1.5 bg-[var(--green-dim)]/60 dark:bg-[var(--green)]/10 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
                Above the line — {above.length}
              </div>
              {above.map((o) => <OutcomeRow key={o.summary.priority.id} outcome={o} />)}
            </>
          )}
          {below.length > 0 && (
            <>
              <div className="px-4 py-1.5 bg-[var(--coral-dim)]/60 dark:bg-[var(--coral)]/10 text-xs font-medium text-[var(--coral)] dark:text-[var(--coral)]">
                Below the line — {below.length}
              </div>
              {groupBelow
                ? belowGroups.map(([reason, group]) => {
                    const expanded = expandedReasons.has(reason);
                    return (
                      <div key={reason} className="border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
                        <button
                          type="button"
                          onClick={() => toggleReason(reason)}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
                        >
                          <span className="h-2.5 w-2.5 rounded-full flex-shrink-0 bg-[var(--coral)]" />
                          <span className="flex-1 min-w-0 text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                            {reason}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)] flex-shrink-0">
                            {group.length}
                          </span>
                          {expanded
                            ? <ChevronUp className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
                            : <ChevronDown className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />}
                        </button>
                        {expanded && (
                          <div className="pl-4 border-l-2 border-[var(--coral)] dark:border-[var(--coral)]/40 ml-5">
                            {group.map((o) => <OutcomeRow key={o.summary.priority.id} outcome={o} />)}
                          </div>
                        )}
                      </div>
                    );
                  })
                : below.map((o) => <OutcomeRow key={o.summary.priority.id} outcome={o} />)}
            </>
          )}
          {filteredOutcomes.length === 0 && (
            <p className="px-4 py-6 text-sm text-[var(--text-ter)] text-center">No priorities match "{query}".</p>
          )}
        </div>
      )}

      {output && outcomes.length === 0 && (
        <div className={`${CARD_CLASS} p-8 text-center`}>
          <CheckCircle2 className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            No active priorities to plan yet. Add items on the prioritization side, then check again.
          </p>
        </div>
      )}

      {/* Levers — what would change the answer */}
      {levers.length > 0 && (
        <div className={`${CARD_CLASS} p-5`}>
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="h-4 w-4 text-[var(--text-ter)]" />
            <h2 className="text-sm font-semibold text-charcoal-600 dark:text-[var(--text-sec)] uppercase tracking-wider">
              What would change the answer
            </h2>
          </div>
          <div className="space-y-3">
            {levers.map((lever) => (
              <div key={lever.key} className="flex items-start gap-3">
                <AlertTriangle className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{lever.label}</p>
                  <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">{lever.detail}</p>
                </div>
                <Link
                  to={lever.href}
                  className="flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline flex-shrink-0"
                >
                  {lever.linkLabel}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* What's asked of your teams — confirm/decline participation (SPEC-02 §3.3).
          The card prunes itself when no participation requirements exist. */}
      {projectId && (
        <ParticipationSummaryCard
          projectId={projectId}
          userName={userName}
          onChanged={() => void checkFeasibility(activeScenario)}
        />
      )}

      {timePhasedEnabled && output && (
        <div className={`${CARD_CLASS} p-5`}>
          <h2 className="text-sm font-semibold text-charcoal-600 dark:text-[var(--text-sec)] uppercase tracking-wider mb-1">
            Can we launch them?
          </h2>
          <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-3">
            Viability tells you what fits in capacity. Launch Calendar adds shared-team GTM windows —
            buildable vs launchable, slip weeks, and &ldquo;say yes to this one&rdquo; counterfactuals.
          </p>
          <Link
            to="/planning/launch-calendar"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            Open Launch Calendar
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}

      {/* Resource utilization */}
      {output && output.resourceSummaries.length > 0 && (
        <div className={`${CARD_CLASS} p-5`}>
          <h2 className="text-sm font-semibold text-charcoal-600 dark:text-[var(--text-sec)] uppercase tracking-wider mb-3">
            Capacity by kind of work
          </h2>
          <div className="space-y-3">
            {output.resourceSummaries.map((rs) => {
              const pct = Math.min(rs.utilizationPercent, 100);
              const over = rs.utilizationPercent > 100;
              return (
                <div key={rs.resourceType}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] capitalize">{rs.resourceType}</span>
                    <span className={over ? 'text-[var(--coral)] dark:text-[var(--coral)] font-medium' : 'text-[var(--text-ter)] dark:text-[var(--text-sec)]'}>
                      {rs.demandWU.toFixed(0)} of {rs.capacityWU.toFixed(0)} WU
                      {over ? ` — over by ${(rs.demandWU - rs.capacityWU).toFixed(0)}` : ''}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--bg)] overflow-hidden">
                    <div
                      className={`h-full rounded-full ${over ? 'bg-[var(--coral)]' : pct > 80 ? 'bg-[var(--mustard)]' : 'bg-[var(--green)]'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scenario comparison — only when there is something to compare */}
      {scenarios.length >= 2 && (
        <div className={`${CARD_CLASS} p-5`}>
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <GitCompareArrows className="h-4 w-4 text-[var(--text-ter)]" />
              <h2 className="text-sm font-semibold text-charcoal-600 dark:text-[var(--text-sec)] uppercase tracking-wider">
                What if we planned differently?
              </h2>
            </div>
            <button
              type="button"
              onClick={() => void compare()}
              disabled={comparing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-sec)] dark:text-[var(--text-pri)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-50"
            >
              {comparing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitCompareArrows className="h-3.5 w-3.5" />}
              Compare {scenarios.length} scenarios
            </button>
          </div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3">
            A scenario is one set of planning assumptions — horizon, buffer, capacity. Comparing
            them shows which priorities become deliverable under each.
          </p>
          {comparison && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {comparison.results.map(({ scenario, output: out, error: runError }) => {
                const tone: FeasibilityTone = out ? feasibilityTone(out.executiveSummary.overallFeasibility) : 'neutral';
                const deliverable = out
                  ? out.prioritySummaries.filter((p) => p.feasibility === 'Fully Achievable' || p.feasibility === 'Partially Achievable').length
                  : 0;
                return (
                  <div
                    key={scenario.id}
                    className={` border p-4 ${scenario.active ? 'border-primary-300 dark:border-primary-700' : 'border-[var(--rule)] dark:border-[var(--rule)]'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{scenario.name}</p>
                      {scenario.active && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">Active</span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                      {PLANNING_MODE_LABELS[scenario.planningMode]} · {scenario.planningWeeks} wk · {Math.round(scenario.capacityBufferPercent * 100)}% buffer
                    </p>
                    {out ? (
                      <>
                        <p className={`text-sm font-semibold mt-2 ${TONE_TEXT[tone]}`}>
                          {FEASIBILITY_PLAIN_LANGUAGE[out.executiveSummary.overallFeasibility]}
                        </p>
                        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
                          {deliverable} of {out.prioritySummaries.length} priorities deliverable
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)] mt-2">{runError}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-3">
            <Link
              to="/planning/engine/compare"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              Full side-by-side comparison
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}

      {/* Drift signals on the approved plan — absent when there is no PoR */}
      {signals && (
        <div className={`${CARD_CLASS} p-5`}>
          <div className="flex items-center gap-2 mb-1">
            <Signal className="h-4 w-4 text-[var(--text-ter)]" />
            <h2 className="text-sm font-semibold text-charcoal-600 dark:text-[var(--text-sec)] uppercase tracking-wider">
              Signals on the approved plan
            </h2>
          </div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3">
            "{signals.cycle.name}"
            {signals.authorizedAt ? ` — authorized ${new Date(signals.authorizedAt).toLocaleDateString()}` : ''}.
            Signals here may suggest re-planning or re-prioritizing.
          </p>
          {openDivergences.length === 0 && openTriggers.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
              <CheckCircle2 className="h-4 w-4" />
              No open signals — the plan holds as approved.
            </div>
          ) : (
            <div className="space-y-2">
              {openDivergences.length > 0 && (
                <div className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                    {openDivergences.length} sequence divergence{openDivergences.length === 1 ? '' : 's'} —
                    execution order differs from the priority order and needs a recorded rationale.
                  </span>
                </div>
              )}
              {openTriggers.length > 0 && (
                <div className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-[var(--coral)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                    {openTriggers.length} re-prioritization recommendation{openTriggers.length === 1 ? '' : 's'} open —
                    planning constraints may be invalidating the current priorities.
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="mt-3">
            <Link
              to={`/planning/plans/${signals.cycle.id}/governance`}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              Open governance
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
